import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { SITE_URL, SITE_NAME } from "@/lib/site";

/**
 * Per-page SEO. Emits a unique title, meta description, canonical URL and
 * Open Graph / Twitter Card tags. Canonical + og:url are ALWAYS built from
 * SITE_URL (production domain), never the serving/preview host.
 *
 * @param {string} title       Full <title> for the page.
 * @param {string} description Meta description (unique per page).
 * @param {string} [keywords]  Optional comma-separated keywords.
 * @param {string} [image]     Absolute OG image URL. Defaults to /og-image.jpg.
 * @param {object} [jsonLd]    Optional Schema.org object injected as JSON-LD.
 */
const SEO = ({ title, description, keywords, image, jsonLd }) => {
  const { pathname } = useLocation();
  const canonical = `${SITE_URL}${pathname === "/" ? "/" : pathname}`;
  const ogImage = image || `${SITE_URL}/og-image.jpg`;

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
};

export default SEO;
